<template>
  <div :class="navigationClasses" class="navigation">
    <div class="navigation__brand">
      <router-link :to="{ name: 'home' }" class="navigation__brand-link">
        <logo class="navigation__brand-icon" />Goliath<small class="navigation__brand-link-version"> {{version}} </small>
        <small class="navigation__brand-link-environment">{{environmentName}}</small>
      </router-link>
    </div>

    <ul class="uk-nav-default uk-nav-parent-icon" uk-nav>
      <li class="uk-nav-divider uk-margin-top uk-margin-bottom"></li>
      <li class="uk-nav-header navigation__header">{{$t('headers.catalog')}}</li>
      <nav-item
        :to="{ name: 'brands' }"
        icon="thumbnails"
      >{{$t('items.brands')}}</nav-item>
      <nav-item
        :to="{ name: 'product-families' }"
        icon="album"
      >{{$t('items.productFamilies')}}</nav-item>
      <nav-item :to="{ name: 'products' }" icon="file">{{$t('items.products')}}</nav-item>
      <nav-item
        :to="{ name: 'product-styles' }"
        icon="bookmark"
      >{{$t('items.productStyles')}}</nav-item>
      <nav-item
        :to="{ name: 'product-designs' }"
        icon="heart"
      >{{$t('items.productDesigns')}}</nav-item>
      <nav-item
        :to="{ name: 'product-categories' }"
        icon="tag"
      >{{$t('items.categories')}}</nav-item>

      <nav-item
        :to="{ name: 'contracts' }"
        icon="file-edit"
      >{{$t('items.contracts')}}</nav-item>
      <nav-item :to="{ name: 'offers' }" icon="cart">{{$t('items.offers')}}</nav-item>

      <nav-item :to="{ name: 'kompa' }" icon="file">{{$t('items.kompa')}}</nav-item>
      <nav-item :to="{ name: 'active-revisions' }" icon="future">{{$t('items.activeRevisions')}}</nav-item>

      <li class="uk-nav-divider uk-margin-top uk-margin-bottom"></li>
      <li class="uk-nav-header navigation__header">{{$t('headers.files')}}</li>
      <nav-item
        :to="{ name: 'attachments' }"
        icon="cloud-upload"
      >{{$t('items.attachments')}}</nav-item>
      <nav-item
        :to="{ name: 'legal-texts' }"
        icon="copy"
      >{{$t('items.legalTexts')}}</nav-item>

      <li class="uk-nav-divider uk-margin-top uk-margin-bottom"></li>
      <li class="uk-nav-header navigation__header">{{$t('headers.settings')}}</li>
      <nav-item :to="{ name: 'tenants' }" icon="server">{{$t('items.tenants')}}</nav-item>
      <nav-item
        :to="{ name: 'organizations' }"
        icon="world"
        v-if="isAuthenticatedAsAdmin"
      >{{$t('items.organizations')}}</nav-item>
      <nav-item
        :to="{ name: 'import-reports' }"
        icon="cloud-download"
        v-if="isAuthenticatedAsAdmin"
      >{{$t('items.imports')}}</nav-item>
      <nav-item
        :to="{ name: 'maintenance' }"
        icon="code"
        v-if="isAuthenticatedAsAdmin"
      >{{$t('items.maintenance')}}</nav-item>
      <nav-item
        :to="{ name: 'queued-jobs' }"
        icon="clock"
        v-if="isAuthenticatedAsAdmin"
      >{{$t('items.queuedJobs')}}</nav-item>
      <nav-item
        :to="{ name: 'users' }"
        icon="users"
        v-if="isAuthenticatedAsAdmin"
      >{{$t('items.users')}}</nav-item>

      <li class="uk-nav-divider uk-margin-top uk-margin-bottom"></li>
      <li class="uk-nav-header navigation__header">{{userName}}</li>
      <li>
        <logout-button/>
      </li>
    </ul>
  </div>
</template>

<script>
import Authentication from '@/mixins/Authentication';
import NavItem from '@/components/NavItem';
import Logo from '@/components/Logo';
import LogoutButton from '@/components/LogoutButton';

export default {
  name: 'navigation',
  mixins: [Authentication],
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    Logo,
    LogoutButton,
    NavItem,
  },
  computed: {
    userName() {
      if (!this.currentUser) return this.$t('anonymous');
      return this.currentUser.name;
    },
    version() { return window.goliath.version; },
    environmentName() {
      const envName = window.goliath.environment;
      return (envName === 'production') ? '' : envName;
    },
    navigationClasses() {
      return {
        'navigation--visible': this.visible,
        [`navigation--${this.environmentName}`]: true,
      };
    },
  },
  i18n: {
    messages: {
      de: {
        anonymous: 'Anonym',
        headers: {
          catalog: 'Portfolio',
          files: 'Dateien',
          settings: 'Einstellungen',
        },
        items: {
          attachments: 'Dateimanager',
          brands: 'Marken',
          categories: 'Kategorien',
          contracts: 'Verträge',
          imports: 'Importe',
          maintenance: 'Maintenance',
          kompa: 'Kompa Liste',
          activeRevisions: 'ActiveRevisions',
          legalTexts: 'Rechtstexte',
          login: 'Anmelden',
          logout: 'Abmelden',
          offers: 'Angebote',
          organizations: 'Vertriebsorganisationen',
          productDesigns: 'Produktdesigns',
          productFamilies: 'Produktfamilien',
          products: 'Produkte',
          productStyles: 'Produktstile',
          publications: 'Veröffentlichungen',
          reports: 'Berichte',
          settings: 'Einstellungen',
          tenants: 'Mandanten',
          users: 'Benutzer',
          queuedJobs: 'Warteschlange',
        },
      },
    },
  },
};
</script>

<style scoped lang="scss">
@import "../theme/variables.scss";

.navigation {
  user-select: none;
  box-sizing: border-box;
  width: $primary-nav-width;
  position: fixed;
  left: -$primary-nav-width;
  top: 0;
  bottom: 0;
  padding: $global-medium-gutter;
  border-right: 1px solid $global-border;
  background-color: $primary-nav-background-color;
  overflow-x: auto;
  opacity: 0;
  transition: {
    property: left, opacity;
    duration: 250ms;
    timing-function: ease-out;
  }
}

.navigation--visible {
  left: 0;
  opacity: 1;
}

.navigation--development,
.navigation--dev {
background-image: linear-gradient(45deg, #f8f8f8 25%, #f2e4f7 25%, #f2e4f7 50%, #f8f8f8 50%, #f8f8f8 75%, #f2e4f7 75%, #f2e4f7 100%);
background-size: 56.57px 56.57px;
}

.navigation--testing {
background-image: linear-gradient(45deg, #f8f8f8 25%, #e4f7e6 25%, #e4f7e6 50%, #f8f8f8 50%, #f8f8f8 75%, #e4f7e6 75%, #e4f7e6 100%);
background-size: 56.57px 56.57px;
}
.navigation--feedtest {
  background-image: linear-gradient(45deg, #f8f8f8 25%, #e4f2f7 25%, #e4f2f7 50%, #f8f8f8 50%, #f8f8f8 75%, #e4f2f7 75%, #e4f2f7 100%);
background-size: 56.57px 56.57px;
}
.navigation--staging {
  background-image: linear-gradient(135deg, #f8f8f8 25%, #eaeaea 25%, #eaeaea 50%, #f8f8f8 50%, #f8f8f8 75%, #eaeaea 75%, #eaeaea 100%);
background-size: 56.57px 56.57px;
}


.navigation__header {
  padding: {
    top: 7px;
    bottom: 7px;
  }
}

.navigation__brand-link {
  text-decoration: none;
  font-size: 26px;
  font-weight: bold;

  &-version {
    font-size: .9rem;
  }

  &-environment {
    display: block;
    font-size: .9rem;
    margin-left: 35px;
    margin-top: -5px;
  }
}

.navigation__brand-icon {
  display: block;
  width: 25px;
  float: left;
  margin: {
    top: 7px;
    right: 8px;
  }
}
</style>



// WEBPACK FOOTER //
// Navigation.vue?07f8a2d0